import { useCallback, useEffect, useMemo, useState } from "react";

import { NetworkStatus, useLazyQuery } from "@apollo/client";

import {
  Device,
  GetDevicesQuery,
  GetDevicesQueryVariables,
  QueryingDevicesByTagQuery,
  QueryingDevicesByTagQueryVariables,
} from "../../../../../API";
import useGetAllLocationsDropdownLoading from "../../../../../common/components/locations/useGetAllLocationsDropdownLoading";
import { useCustomerIdGuard } from "../../../../../common/hooks/useCustomerIdGuard";
import { RouteEnum } from "../../../../../common/models/enums";
import {
  GET_DEVICES,
  QUERYING_DEVICES_BY_TAG,
} from "../../../../../common/operations/queries";
import { DEFAULT_LIMIT_COUNT } from "../../../../../common/variables/common";
import { errorNotification } from "../../../../../common/variables/notification";
import {
  useSelectedLocationDropdown,
  useSelectedTagIdDropdown,
} from "../../../../../common/variables/selectedLocationDropdown";

export const useGetLiveViewData = () => {
  const selectedCustomerId = useCustomerIdGuard();
  const selectedTagId = useSelectedTagIdDropdown();
  const locationVariable = useSelectedLocationDropdown();

  const { locationsDropdownLoading } = useGetAllLocationsDropdownLoading();

  const isDevicesPage = window.location.pathname === RouteEnum.Devices;

  const [customLoading, setCustomLoading] = useState(false);

  const [getDevicesByTag, { data: dataByTag, loading: loadingByTag }] =
    useLazyQuery<QueryingDevicesByTagQuery, QueryingDevicesByTagQueryVariables>(
      QUERYING_DEVICES_BY_TAG,
      {
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
      }
    );

  const [getDevices, { data, fetchMore, networkStatus, loading, refetch }] =
    useLazyQuery<GetDevicesQuery, GetDevicesQueryVariables>(GET_DEVICES, {
      fetchPolicy: "cache-first",
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    setCustomLoading(true);

    const fetchInitialDevices = async () => {
      try {
        await getDevices({
          variables: {
            limit: DEFAULT_LIMIT_COUNT,
            customerId: selectedCustomerId,
            locationId: locationVariable?.value,
          },
        });
      } catch (error: any) {
        console.error(`Error while fetching devices: ${error}`);

        errorNotification();
      } finally {
        setCustomLoading(false);
      }
    };

    const fetchInitialDevicesByTag = async () => {
      try {
        await getDevicesByTag({
          variables: {
            tagId: selectedTagId ?? "",
          },
        });
      } catch (error: any) {
        console.error(`Error while fetching devices by tag: ${error}`);
      } finally {
        setCustomLoading(false);
      }
    };

    if (selectedTagId) {
      fetchInitialDevicesByTag();
    } else if (selectedCustomerId) {
      fetchInitialDevices();
    } else {
      setCustomLoading(false);
    }
  }, [
    selectedCustomerId,
    locationVariable?.value,
    getDevices,
    getDevicesByTag,
    selectedTagId,
  ]);

  const loadMoreDevices = useCallback(() => {
    if (data?.getDevices.nextToken && !loading) {
      fetchMore({
        variables: {
          limit: DEFAULT_LIMIT_COUNT,
          customerId: selectedCustomerId,
          locationId: locationVariable?.value,
          nextToken: data.getDevices.nextToken,
        },
      }).catch(error => {
        console.error(`Error while fetching more devices: ${error}`);

        errorNotification();
      });
    }
  }, [data, fetchMore, selectedCustomerId, locationVariable?.value, loading]);

  // Auto-load more devices when on devices page
  useEffect(() => {
    if (isDevicesPage && data?.getDevices?.nextToken && !loading) {
      console.log("Devices page: Auto-loading more devices");

      loadMoreDevices();
    }
  }, [isDevicesPage, data?.getDevices?.nextToken, loading, loadMoreDevices]);

  const devices: Device[] = useMemo(
    () => (data?.getDevices?.items as Device[]) || [],
    [data?.getDevices?.items]
  );

  const devicesByTag: Device[] = useMemo(
    () => (dataByTag?.queryingDevicesByTag?.items as Device[]) || [],
    [dataByTag?.queryingDevicesByTag?.items]
  );

  const dataLoading =
    locationsDropdownLoading ||
    customLoading ||
    (selectedTagId ? loadingByTag : loading);

  return {
    devices: selectedTagId ? devicesByTag : devices,
    loading: dataLoading,
    refetch,
    fetchMoreLoading: networkStatus === NetworkStatus.fetchMore,
    loadMoreDevices,
    nextToken: data?.getDevices.nextToken,
  } as const;
};
